import { Link, useParams } from 'react-router-dom'
import { useEffect } from 'react'
import { AssignmentData, useAssignments } from '../../hooks/useAssignment'
import { useProjectById } from '../../hooks/useProjectById'
import { Button } from 'antd'
import { Answer, FLOW_TYPES } from '../../Types'
import { getPrioritisedLanguage } from '../../util/languages'

import { COLOR } from '../../constants'
import { deriveCurrentWalk } from './dependency-view/dependencyUtils'
import { logAssignmentDebug } from './logAssignmentDebug'
import { QuestionHeader } from '../../components/question-answer/QuestionHeader'
import { AnswerHistory } from '../../components/question-answer/AnswerHistory'
import { AnswerFactory } from '../../components/question-answer/AnswerFactory'
import { AnswerSubmissionDetails } from '../../components/question-answer/AnswerSubmissionDetails'

// Where do we go after `q`? Answered radio → the chosen option's destination.
// Anything else (non-radio, or unanswered radio) → next[0]: we don't know
// which option the participant will pick later, so assume the first one.

export const SubjectAssignment = ({ subjectLanguageIsoCode }: { subjectLanguageIsoCode: string }) => {
  const { projectId, subjectId, assignmentId } = useParams() as {
    projectId: string
    subjectId: string
    assignmentId: string
  }

  const { data: projectData } = useProjectById(projectId)
  const { data } = useAssignments(projectId, assignmentId, subjectId)

  // If one of the prioritised languages is present in the project, use that
  // - It will be checked on question level if a translation for this language exist
  const prioritisedLanguageIsoCode = getPrioritisedLanguage(projectData.languages)

  const assignment: AssignmentData = data.assignment ? data.assignment : data

  // startId is only passed when it's a dependency assignment
  const walk = deriveCurrentWalk(assignment, data.startId)

  const debugLang = (prioritisedLanguageIsoCode ?? subjectLanguageIsoCode).toLowerCase()
  useEffect(() => {
    logAssignmentDebug(assignment, debugLang, 'SubjectAssignment')
  }, [assignment, debugLang])

  return (
    <>
      {assignment.flowType === FLOW_TYPES.DEPENDENCY ? (
        <div className="container-fluid py-2 mb-1" style={{ background: COLOR.LIGHT_GRAY }}>
          <div className="row ">
            <div className="col-10 py-2 px-4 ">
              <h6 className="fw-bold mb-1">Questionnaire details</h6>
              <p className="opacity-75">Explore the dependency tree and review historical answers.</p>
            </div>
            <div className="col-2 d-flex justify-content-end align-items-end pb-3">
              <Link to="dependency" className="w-100">
                <Button type="primary" size="small" className="w-100">
                  <p className="p-small my-0">Show</p>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      ) : null}
      <div className="container-fluid pt-4 px-4">
        {walk.map(({ question, answers }, index) => {
          const languageIsoCode =
            prioritisedLanguageIsoCode && question.title[prioritisedLanguageIsoCode]
              ? prioritisedLanguageIsoCode
              : subjectLanguageIsoCode.toLowerCase()

          const title = question.title[languageIsoCode]
          const body = question.body[languageIsoCode]

          return (
            <div key={index} className="row mb-5 ">
              <QuestionHeader
                key={index}
                title={title}
                body={body}
                isAnswered={answers.length > 0}
                isOffline={answers.length > 0 ? !!answers[0].offline : false}
              ></QuestionHeader>
              <AnswerExist answers={answers} question={question} languageIsoCode={languageIsoCode}></AnswerExist>
            </div>
          )
        })}
      </div>
    </>
  )
}

const AnswerExist = ({ answers, question, languageIsoCode }) => {
  const primaryAnswer: Answer = answers[0]

  if (!primaryAnswer) {
    return <div className="col-12 pb-4 " style={{ borderBottom: '1px solid #BDBDBD' }}></div>
  }

  return (
    <div className="col-12 mt-1 " style={{ borderBottom: '1px solid #A5A5A5' }}>
      <div className="mt-2">
        {/*
             This is the actual answer - either info, radio, unitcounter or nprs
        */}
        <AnswerFactory
          answer={answers[0]}
          question={question}
          defaultLang={languageIsoCode}
          variant="PRIMARY"
        ></AnswerFactory>
      </div>
      <div className="mt-2">
        <div className="pt-2 pb-3">
          {/*
              THis is the timestamp and indication if colected while offline
          */}
          <AnswerSubmissionDetails
            createdAt={primaryAnswer.createdAt}
            deviceTime={primaryAnswer.deviceTime}
            isOffline={!!primaryAnswer.offline}
            index={0}
            isPrimary={true}
          ></AnswerSubmissionDetails>
        </div>
        {/*
             This is the all answers execpt the primary answer
             The primary is the latest answer - everything else is history
        */}
        <AnswerHistory answers={answers} question={question} defaultLang={languageIsoCode}></AnswerHistory>
      </div>
    </div>
  )
}
